import React, { useRef, useState, useEffect } from 'react';
import PageConfig from '@/component/layout/PageConfig.tsx';
import DashboardMap from '@/pages/dashboard/DashboardMapDefault.tsx';
import useQueryGetUsers, { DeviceStatus } from '@/api/user/getUsers.ts';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetAlertBioLive from '@/api/notification/getAlertBioLive.ts';
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts';
import { useTranslation } from 'react-i18next';
import useQueryGetAlertsUnprocessed, { useMutationProcessAlerts, useQueryGetSafeZoneStatus } from '@/api/dashboard/alerts.ts';
import useQueryGetSettings from '@/api/settings/settings.ts';
import { showNotificationBanner } from '@/util/webviewBridge';

type IProps = Record<string, never>;

enum AlertStatus {
    EMERGENCY = 'emergency',
    BIO = 'bio',
    FALL = 'fall',
}

const Dashboard: React.FC<IProps> = () => {
    const { hasPermission } = useQueryGetMyPermission();
    const {
        data: _bioAlertRes,
        refetch: refetchBioAlerts
    } = useQueryGetAlertBioLive(hasPermission(PermissionSubject.ALERT_BIO, PermissionAction.READ));

    const { data: users, refetch: refetchUsers } = useQueryGetUsers();
    const { data: me } = useQueryGetMe();

    const mapRef = useRef<any>();

    const [expandedStatus, setExpandedStatus] = useState<string | null>(null);
    const [safeZoneLocation, setSafeZoneLocation] = useState<{ lat: number; lng: number } | null>(null); // 안심구역 상태 추가
    const [showSafeZone, setShowSafeZone] = useState<boolean>(false); // 안심구역 표시 여부 추가
    const [showMyLocation, setShowMyLocation] = useState<boolean>(false); // 내위치 표시 여부 추가
    
    // 이전 알림 상태를 저장하기 위한 ref
    const previousAlertDataRef = useRef<any>(null);

    // const isTestUser = me?.email === 'test@test.com';

    const { t } = useTranslation();

    const { mutate: processAlert } = useMutationProcessAlerts();

    const { data: alertData = {} } = useQueryGetAlertsUnprocessed();

    const { data: settingsData } = useQueryGetSettings(); // 설정 데이터 가져오기

    // 설정 데이터가 있을 때 안심구역 위치 자동 설정
    useEffect(() => {
        if (settingsData?.latitude && settingsData?.longitude) {
            setSafeZoneLocation({ lat: settingsData.latitude, lng: settingsData.longitude });
        }
    }, [settingsData]);

    // 🔔 실시간 알림 감지 및 안드로이드 배너 알림 호출
    useEffect(() => {
        if (!alertData || !previousAlertDataRef.current) {
            previousAlertDataRef.current = alertData;
            return;
        }

        const previousData = previousAlertDataRef.current;
        
        // 긴급 알림 체크
        const previousEmergencyCount = previousData.emergency?.length || 0;
        const currentEmergencyCount = alertData.emergency?.length || 0;
        
        if (currentEmergencyCount > previousEmergencyCount) {
            const newEmergencyAlerts = alertData.emergency?.slice(previousEmergencyCount) || [];
            
            newEmergencyAlerts.forEach((alert: any) => {
                const title = "🚨 " + t('dashboard.alert.emergency');
                const message = `${alert.protectee?.name || t('common.user')}님이 긴급버튼을 눌렀습니다`;
                
                showNotificationBanner(title, message, 'emergency');
            });
        }

        // 생체이상 알림 체크
        const previousBioCount = previousData.bio?.length || 0;
        const currentBioCount = alertData.bio?.length || 0;
        
        if (currentBioCount > previousBioCount) {
            const newBioAlerts = alertData.bio?.slice(previousBioCount) || [];
            
            newBioAlerts.forEach((alert: any) => {
                const title = "💓 " + t('dashboard.alert.bio');
                const message = `${alert.protectee?.name || t('common.user')}님의 생체신호가 비정상입니다`;
                
                showNotificationBanner(title, message, 'bio');
            });
        }

        // 낙상감지 알림 체크
        const previousFallCount = previousData.fall?.length || 0;
        const currentFallCount = alertData.fall?.length || 0;
        
        if (currentFallCount > previousFallCount) {
            const newFallAlerts = alertData.fall?.slice(previousFallCount) || [];
            
            newFallAlerts.forEach((alert: any) => {
                const title = "🤕 " + t('dashboard.alert.falldetection');
                const message = `${alert.protectee?.name || t('common.user')}님에게 낙상이 감지되었습니다`;
                
                showNotificationBanner(title, message, 'falldetection');
            });
        }

        // 현재 상태를 이전 상태로 업데이트
        previousAlertDataRef.current = alertData;
        
    }, [alertData]);

    // me 데이터가 로드되면 이전 위치로 지도 이동
    useEffect(() => {
        if (me && mapRef.current) {
            // localStorage에서 위치 정보 가져오기
            const centerY = Number(localStorage.getItem('dashboard_map__center__latitude'));
            const centerX = Number(localStorage.getItem('dashboard_map__center__longitude'));
            const zoomLevel = Number(localStorage.getItem('dashboard_map__zoom')) || 18;
            
            // 유효한 위치 정보가 있으면 지도 이동
            if (centerY && centerX) {
                // 약간의 지연을 주어 지도가 완전히 로드된 후 이동하도록 함
                setTimeout(() => {
                    mapRef.current.setLocation(centerY, centerX, zoomLevel);
                }, 500);
            }
        }
    }, [me]);

    // 알림 상태 변경 시 즉시 데이터 갱신
    useEffect(() => {
        const interval = setInterval(() => {
            Promise.all([
                refetchBioAlerts(),
                refetchUsers()
            ]);
        }, 2000); // 5초에서 2초로 변경

        return () => clearInterval(interval);
    }, [refetchBioAlerts, refetchUsers]);

    // 알림 상태 목록
    const statusList = [
        { type: AlertStatus.EMERGENCY, label: t('dashboard.alert.emergency'), color: 'red' },
        { type: AlertStatus.BIO, label: t('dashboard.alert.bio'), color: 'orange' },
        { type: AlertStatus.FALL, label: t('dashboard.alert.falldetection'), color: 'purple' },
    ];



    // refetchInterval과 staleTime을 낮춰 더 자주 갱신하도록 설정
    const { data: safeZoneData, isLoading, refetch: refetchSafeZoneStatus } = useQueryGetSafeZoneStatus();

    // 🔔 안심구역 이탈 상태 변경 감지 (safeZoneData 선언 이후로 이동)
    const previousSafeZoneStatusRef = useRef<string | null>(null);
    useEffect(() => {
        const currentSafeZoneStatus = safeZoneData?.isSafeZone === true
            ? 'safe'
            : safeZoneData?.isSafeZone === false
                ? 'exit'
                : 'unknown';

        // 이전 상태가 있고, 안심 상태에서 이탈 상태로 변경된 경우에만 알림
        if (previousSafeZoneStatusRef.current && 
            previousSafeZoneStatusRef.current === 'safe' && 
            currentSafeZoneStatus === 'exit') {
            
            showNotificationBanner(
                "📍 " + t('dashboard.alert.safeZoneExit'),
                t('dashboard.alert.safeZoneExit'),
                'safezone'
            );
        }

        previousSafeZoneStatusRef.current = currentSafeZoneStatus;
    }, [safeZoneData?.isSafeZone]);

    // 페이지 진입 시와 localStorage 변경 시 안심구역 상태 강제 갱신
    useEffect(() => {
        refetchSafeZoneStatus();
        
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "safeZoneRadius") {
                refetchSafeZoneStatus();
            }
        };
        
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [refetchSafeZoneStatus]);

    // 데이터가 `undefined`일 때는 로딩 상태 유지
    if (isLoading || safeZoneData === undefined) {
        return null; // UI 렌더링을 막아 불필요한 깜빡임 방지
    }

    // 상태 값 변환 로직
    const safeZoneStatus = safeZoneData?.isSafeZone === true
        ? 'safe'  // "안심"
        : safeZoneData?.isSafeZone === false
            ? 'exit'  // "이탈"
            : 'unknown';  // "알 수 없음"

    return (
        <PageConfig showTopNav={true} showFooter={true} className={'w-full h-full'}>
            <div className="relative w-full h-full">
                <DashboardMap 
                    className={'absolute inset-0 w-full h-full'} 
                    ref={mapRef} 
                    safeZoneLocation={safeZoneLocation}
                    showSafeZone={showSafeZone}
                    showMyLocation={showMyLocation} // 내위치 표시 여부 전달
                />
                {/* 알림 상태 표시 */}
                <div className={'absolute top-12 left-4 flex flex-col gap-4'}>
                    {statusList.map(({ type, label, color }) => {
                        const count = alertData[type]?.length || 0;
                        return (
                            <div key={type} className={'relative flex items-center'}>
                                <div
                                    className={`w-12 h-12 ${count > 0 ? `bg-${color}-500` : 'bg-gray-300'} rounded-full flex items-center justify-center cursor-pointer transition-all duration-300`}
                                    onClick={() => setExpandedStatus(expandedStatus === type ? null : type)}
                                >
                                    <span className={'text-white font-bold'}>{count}</span>
                                </div>
                                {expandedStatus === type && count > 0 && (
                                    <div className={`ml-4 px-4 py-2 bg-${color}-500 text-white rounded-lg flex items-center shadow-lg transition-all duration-300`}>
                                        <span className={'font-semibold'} dangerouslySetInnerHTML={{ __html: label }} />
                                        {type === AlertStatus.EMERGENCY && (
                                            <button
                                                className={`ml-2 bg-white text-red-500 px-2 py-1 rounded-md`}
                                                onClick={() => processAlert(type)}
                                            >
                                                {t('dashboard.button.confirm')}
                                            </button>
                                        )}
                                        {type === AlertStatus.BIO && (
                                            <button
                                                className={`ml-2 bg-white text-orange-500 px-2 py-1 rounded-md`}
                                                onClick={() => processAlert(type)}
                                            >
                                                {t('dashboard.button.confirm')}
                                            </button>
                                        )}
                                        {type === AlertStatus.FALL && (
                                            <button
                                                className={`ml-2 bg-white text-purple-500 px-2 py-1 rounded-md`}
                                                onClick={() => processAlert(type)}
                                            >
                                                {t('dashboard.button.confirm')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div
                        className={`w-14 h-14 flex items-center justify-center rounded-full transition-all duration-300 text-white font-bold
                            ${
                                safeZoneStatus === 'safe'
                                    ? 'bg-green-500 shadow-md' // 안심: 초록 배경 + 그림자 효과
                                    : safeZoneStatus === 'exit'
                                        ? 'bg-black border-4 border-cyan-300 animate-pulse' // 이탈: 검정 배경 + 네온 테두리 애니메이션 추가
                                        : 'bg-gray-500 opacity-80' // 알 수 없음: 회색 배경 (투명도 80%)
                             }`}
                    >
                        {safeZoneStatus === 'safe' ? t('dashboard.safeZone.safe') :
                            safeZoneStatus === 'exit' ? t('dashboard.safeZone.exit') :
                                <span dangerouslySetInnerHTML={{ __html: t('dashboard.safeZone.unknown') }} />}
                    </div>
                </div>

                {me && me?.latitude && me?.longitude && (
                    <div className={'absolute bottom-40 right-4 flex flex-col gap-4'}>
                        {/* 사용자 버튼 */}
                        <button
                            className={'w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg'}
                            onClick={() => {
                                // 온라인 상태의 사용자 중 첫 번째 사용자 찾기
                                const targetUser = users?.find(user => user.device?.status === DeviceStatus.ONLINE) || users?.[0];

                                if (targetUser && targetUser.location?.latitude && targetUser.location?.longitude) {
                                    mapRef?.current?.setLocation(targetUser.location.latitude, targetUser.location.longitude, 18);
                                } else if (me.latitude && me.longitude) {
                                    mapRef?.current?.setLocation(me.latitude, me.longitude, 18);
                                }
                            }}
                        >
                            {t('dashboard.button.user')}
                        </button>

                        {/* 안심구역 버튼 */}
                        <button
                            className={`w-12 h-12 ${showSafeZone ? 'bg-red-500' : 'bg-green-600'} text-white rounded-full flex items-center justify-center shadow-lg`}
                            onClick={() => {
                                // 토글 기능 구현: 이미 표시 중이면 숨기기
                                if (showSafeZone) {
                                    setShowSafeZone(false);
                                    return;
                                }
                                
                                // 설정에서 가져온 위치 정보 우선 사용
                                if (settingsData?.latitude && settingsData?.longitude) {
                                    setSafeZoneLocation({ lat: settingsData.latitude, lng: settingsData.longitude }); // 안심구역 위치 설정
                                    
                                    // 반경에 따른 줌 레벨 계산 (반경이 클수록 줌 레벨이 작아짐)
                                    const radius = Number(localStorage.getItem("safeZoneRadius")) || 100;
                                    const zoomLevel = Math.max(10, Math.min(18, Math.floor(18 - (Math.log2(radius / 100)))));
                                    
                                    mapRef?.current?.setLocation(settingsData.latitude, settingsData.longitude, zoomLevel); // 지도 이동
                                    setShowSafeZone(true); // 안심구역 표시
                                } 
                                // 설정 정보가 없으면 현재 위치 사용
                                else if (me.latitude && me.longitude) {
                                    setSafeZoneLocation({ lat: me.latitude, lng: me.longitude }); // 안심구역 위치 설정
                                    
                                    // 반경에 따른 줌 레벨 계산
                                    const radius = Number(localStorage.getItem("safeZoneRadius")) || 100;
                                    const zoomLevel = Math.max(10, Math.min(18, Math.floor(18 - (Math.log2(radius / 100)))));
                                    
                                    mapRef?.current?.setLocation(me.latitude, me.longitude, zoomLevel); // 지도 이동
                                    setShowSafeZone(true); // 안심구역 표시
                                }
                            }}
                        >
                            <span className="text-center" dangerouslySetInnerHTML={{ __html: t('dashboard.button.safeZone') }} />
                        </button>


                        {/* 내위치 버튼 */}
                        <button
                            className={`w-12 h-12 ${showMyLocation ? 'bg-cyan-500' : 'bg-gray-600'} text-white rounded-full flex items-center justify-center shadow-lg`}
                            onClick={() => {
                                if (showMyLocation) {
                                    // 이미 표시 중이면 토글 끄기
                                    setShowMyLocation(false);
                                    // 사용자 위치 마커 제거
                                    mapRef.current?.clearUserLocation();
                                    return;
                                }
                                
                                if (navigator.geolocation) {
                                    navigator.geolocation.getCurrentPosition(
                                        (position) => {
                                            const { latitude, longitude } = position.coords;
                                            if (mapRef.current) {
                                                mapRef.current.setLocation(latitude, longitude, 18);
                                                mapRef.current.setUserLocation(latitude, longitude); // 사용자 위치 마커 추가
                                                setShowMyLocation(true); // 내위치 토글 켜기
                                            }
                                        },
                                        (_error) => {
                                            alert(`${t('dashboard.error.locationUnavailable')}`);
                                        }
                                    );
                                } else {
                                    alert(`${t('dashboard.error.unsupportedBrowser')}`);
                                }
                            }}
                        >
                            {t('dashboard.button.myLocation')}
                        </button>

                    </div>
                )}
            </div>
        </PageConfig>
    );
};

export default Dashboard;
